import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { IoIosRefresh } from "react-icons/io";

import Layout from "../reusable-components/Layout";
import { useAppContext } from "../context/ThemeContext";
import { getAllData } from "../utilities/firebase-connection";
import { PostListComponent } from "../reusable-components/PostListComponent";
import WordRotator from "../reusable-components/custom-component/WordRotator";

function Blog() {
	const { siteTheme, state } = useAppContext();
	const [posts, setPosts] = useState([]);
	const [filteredPost, setFilteredPosts] = useState([]);
	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(false);
	const [noResult, setNoResult] = useState(false);
	const inputRef = useRef(null);
	const showForm = posts?.length > 5;

	let isDay = state.isDay;
	if (JSON.parse(sessionStorage.getItem("isDay"))) {
		isDay = JSON.parse(sessionStorage.getItem("isDay"));
	}

	useEffect(() => {
		let loadId;
		(async () => {
			setLoading(true);
			const posts = await getAllData("posts");
			setPosts(
				posts.sort((a, b) => {
					// Assumes 'timestamp' is a Firestore Timestamp object
					return b.publishDate?.toMillis?.() - a.publishDate?.toMillis?.();
				})
			);

			loadId = setTimeout(() => {
				setLoading(false);
			}, 2000);
		})();

		return () => clearTimeout(loadId);
	}, []);

	function handleSearch(e) {
		e.preventDefault();
		const searchWords = query?.trim()?.toLowerCase()?.split(/\s+/);
		const filteredPosts = posts.filter((post) =>
			searchWords.some((word) =>
				post?.post?.title?.toLowerCase().includes(word)
			)
		);

		if (filteredPosts?.length) {
			setFilteredPosts(filteredPosts);
			setQuery("");
			setNoResult(false);
		} else {
			setNoResult(true);
			setQuery("");
		}

		if (inputRef.current) {
			inputRef.current.blur();
		}
	}

	let renderPost = posts;
	if (filteredPost?.length) {
		renderPost = filteredPost;
	}

	function handleReset() {
		if (filteredPost?.length !== 0) setFilteredPosts([]);
	}
	return (
		<>
			<Helmet>
				<title>Wiingr | Blog</title>
			</Helmet>
			<Layout>
				<div
					className={`${siteTheme.textColor} ease-in duration-300 relative flex flex-col w-full h-[100vh] sm:px-4 mx-auto max-w-max-content-width pb-20 overflow-y-scroll overflow-x-hidden no-scrollbar`}>
					{posts?.length === 0 ? (
						<div className="mx-auto mt-24 font-thin tracking-wider w-fit font-saira">
							<p
								className={`text-center px-4 py-1 flex flex-col items-center gap-2 rounded-lg ${siteTheme.toolText} ${siteTheme.toolBackground}`}>
								<div>
									<span>
										It looks like there aren’t any posts here just yet. Once
										something is shared, it will show up here!
									</span>
								</div>
							</p>
						</div>
					) : (
						<div className={`${showForm ? "mt-36" : "mt-16"}`}>
							{showForm && (
								<form
									onSubmit={handleSearch}
									className="fixed z-10 top-[64px] sm:top-[82px] ml-2 sm:ml-4 w-full flex flex-col">
									<div className="flex flex-row items-center gap-2">
										<input
											type="text"
											name="search"
											ref={inputRef}
											value={query}
											placeholder="Search..."
											className={`${siteTheme.textColor} ${
												noResult ? "border-red-500" : siteTheme.borderColor
											} backdrop-blur-lg bg-transparent border-[0.5px] ${
												isDay
													? "focus:border-linesLighter"
													: "focus:border-linesLight"
											} w-2/3 sm:w-1/2 xl:w-1/4 text-sm font-semibold tracking-wider outline-none focus:outline-none focus:ring-0 rounded-full`}
											id="search"
											required
											autocomplete="off"
											onChange={(e) => setQuery(e.target.value)}
										/>
										<div
											onClick={handleReset}
											className={`${siteTheme.toolText} ${
												filteredPost?.length !== 0 && siteTheme.toolBackground
											} rounded-full p-[3px] w-4 h-4 flex items-center justify-center cursor-pointer`}>
											<IoIosRefresh size={16} />
										</div>
									</div>
									{noResult && (
										<span className="mt-[2px] ml-3 text-xs text-red-500">
											No result found
										</span>
									)}
								</form>
							)}
							<div
								className={`w-full lg:w-1/3 lg:max-w-1/3 mx-auto font-saira fixed z-10 top-[116px] sm:top-[130px] rounded-full ${siteTheme.background}`}>
								<WordRotator
									words={[
										"-->> Nexus Blog – Insights at the Intersection of Tech and Innovation",
										"-->> Ideas, Innovation, and Industry Trends – Powered by Wiingr",
										"-->> Code. Connect. Converge.",
										"-->> The Pulse of Progress – Stories from Wiingr",
										"-->> Wiingr Nexus – Thought Leadership in the Digital Age",
										"-->> Behind the Screens – Tech Talk from Wiingr Nexus",
									]}
									wrapperstyles={`sm:ml-5 w-[90%] h-[1.5rem] ml-3 lg:w-full`}
									textstyles="font-[200] text-sm"
								/>
							</div>
							<div className="relative gap-5 font-thin font-saira columns-1 lg:columns-2">
								{renderPost.map((post) => (
									<PostListComponent
										key={post?.id}
										data={{
											id: post?.id,
											title: post?.post?.title,
											paragraph: post?.post?.paragraph,
											timestamp: post?.publishDate,
											socials: post?.post?.socials,
											language: post?.post?.language,
											image: post?.post?.image,
											codeSnippet: post?.post?.codeSnippet,
											newFor: post?.newFor,
											readTime: post?.readTime,
											likes: post?.likes,
											views: post?.views,
										}}
										loading={loading}
										siteTheme={siteTheme}
										isDay={isDay}
									/>
								))}
							</div>
						</div>
					)}
				</div>
			</Layout>
		</>
	);
}

export default Blog;
